import React, { createContext, useContext, useEffect, useState } from 'react';

import { FEATURE_FLAG } from '@qality/common-config';
import { TestType } from '@qality/common-model';
import { FlagService, TestTypeService } from '@qality/common-service';
import { DEFAULT_TEST_TYPE } from '@qality/common-utils/src/TestType.const';

type TestTypeContextValue = {
  testTypes: Array<TestType>;
  isLoading: boolean;
  getTestType: (id: number) => TestType;
};

export const initialContextValue: TestTypeContextValue = {
  testTypes: [],
  isLoading: false,
  getTestType: () => null,
};

export const TestTypeContext =
  createContext<TestTypeContextValue>(initialContextValue);

export const useTestTypesContext = (): TestTypeContextValue => {
  return useContext(TestTypeContext);
};

type Props = {
  children: React.ReactNode;
};

export default function TestTypeProvider({ children }: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);

  const [testTypes, setTestTypes] = useState<Array<TestType>>([]);

  const initPremiumCloud = async () => {
    setIsLoading(true);
    try {
      const fetchedTestTypes = await TestTypeService.getTestTypes();
      setTestTypes(fetchedTestTypes);
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        "We couldn't get test types. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const initFreeAndServer = () => {
    setTestTypes([DEFAULT_TEST_TYPE]);
    setIsLoading(false);
  };

  const getTestType = (id: number): TestType => {
    return testTypes.find((testType) => testType.id === id);
  };

  useEffect(() => {
    FEATURE_FLAG.isPremium ? initPremiumCloud() : initFreeAndServer();
  }, []);

  return (
    <TestTypeContext.Provider
      value={{
        testTypes,
        isLoading,
        getTestType,
      }}
    >
      {children}
    </TestTypeContext.Provider>
  );
}
